import React, { useContext, memo } from 'react';
import type { FormControl } from '@atlas-forms/types-js';
import { FormOperatingModeContext } from '../../contexts/FormOperatingModeContext';
import { getInputClassName } from '../utils/classNameUtils';

export interface ButtonControlProps {
  control: FormControl;
  value?: any;
  onChange?: (value: any) => void;
  error?: string;
  touched?: boolean;
  mode?: string;
  onSubmit?: () => void;
}

const VARIANT_STYLES: Record<string, React.CSSProperties> = {
  primary:   { background: '#16a34a', color: '#fff', border: 'none' },
  secondary: { background: 'transparent', color: '#374151', border: '1px solid #d1d5db' },
  danger:    { background: '#ef4444', color: '#fff', border: 'none' },
  success:   { background: '#22c55e', color: '#fff', border: 'none' },
  warning:   { background: '#f59e0b', color: '#fff', border: 'none' },
  ghost:     { background: 'transparent', color: '#374151', border: '1px solid transparent' },
  link:      { background: 'transparent', color: '#2563eb', border: 'none', padding: '0', minWidth: 'unset', textDecoration: 'underline' },
  default:   { background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db' },
};

const VARIANT_STYLES_DISABLED: Record<string, React.CSSProperties> = {
  primary:   { background: '#6b7280', color: '#fff', border: 'none' },
  secondary: { background: 'transparent', color: '#9ca3af', border: '1px solid #d1d5db' },
  danger:    { background: '#6b7280', color: '#fff', border: 'none' },
  success:   { background: '#6b7280', color: '#fff', border: 'none' },
  warning:   { background: '#6b7280', color: '#fff', border: 'none' },
  ghost:     { background: 'transparent', color: '#9ca3af', border: '1px solid transparent' },
  link:      { background: 'transparent', color: '#9ca3af', border: 'none', padding: '0', minWidth: 'unset', textDecoration: 'underline' },
  default:   { background: '#f3f4f6', color: '#9ca3af', border: '1px solid #d1d5db' },
};

const ButtonControl = memo(({
  control,
  onChange,
  error,
  touched,
  mode,
}: ButtonControlProps) => {
  const operatingMode = useContext(FormOperatingModeContext);
  const isReadOnly = mode === 'view' || operatingMode.mode === 'view' || control.readonly;

  const buttonType = (control.config?.type as string) || 'button';
  const isSubmit = buttonType === 'submit';
  const variant = (control.config?.variant as string) || 'primary';

  const variantMap = isReadOnly ? VARIANT_STYLES_DISABLED : VARIANT_STYLES;
  const variantStyle = variantMap[variant] ?? variantMap['default'];

  const buttonStyle: React.CSSProperties = {
    padding: variant === 'link' ? '0' : '10px 20px',
    borderRadius: variant === 'link' ? 0 : 6,
    fontSize: 14,
    fontWeight: 600,
    cursor: isReadOnly ? 'not-allowed' : 'pointer',
    transition: 'all 0.15s ease',
    opacity: isReadOnly ? 0.6 : 1,
    minWidth: variant === 'link' ? 'unset' : 120,
    ...variantStyle,
  };

  const handleClick = (e: React.MouseEvent) => {
    if (isReadOnly) return;
    if (!isSubmit) {
      e.preventDefault();
    }
    if (!isSubmit) {
      onChange?.({ timestamp: new Date().toISOString() });

      const action = (control.config?.action as string) || null;
      if (action === 'navigate' && control.config?.url) {
        const url = control.config.url as string;
        const target = (control.config?.target as string) || '_self';
        if (target === '_blank') {
          window.open(url, '_blank', 'noopener,noreferrer');
        } else {
          window.location.href = url;
        }
      }
    }
  };

  return (
    <div className={`button-control-wrapper ${control.hidden ? 'hidden' : ''}`}>
      <button
        type={isSubmit ? 'submit' : 'button'}
        onClick={handleClick}
        disabled={isReadOnly}
        style={buttonStyle}
        className={getInputClassName({
          baseClass: 'button-control',
          hasError: !!error && touched,
          isReadOnly,
        })}
        aria-label={control.label}
        title={control.helpText}
      >
        {control.label || 'Button'}
      </button>

      {error && touched && (
        <div className="af-form-field__error" style={{ marginTop: 4, fontSize: 12, color: '#ef4444' }}>
          {error}
        </div>
      )}
    </div>
  );
}) as React.FC<ButtonControlProps>;

ButtonControl.displayName = 'ButtonControl';

export default ButtonControl;
